import React, { useState } from 'react';
import { Sparkles, Activity, Heart, Shield, ArrowRight, Check, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { services } from '../data/mock';
import { useAppointment } from '../context/AppointmentContext';

const iconMap = {
  Sparkles: Sparkles,
  Activity: Activity,
  Heart: Heart,
  Shield: Shield
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const { openAppointmentModal } = useAppointment();

  const openServiceModal = (service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Advanced Non-Surgical Treatments
          </h2>
          <p className="text-xl text-gray-600">
            Cutting-edge regenerative medicine therapies that promote natural healing without surgery
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-500 border-gray-200 hover:border-[#E63946] overflow-hidden bg-white"
              >
                {/* Service Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B3A]/50 via-[#1B1B3A]/10 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="bg-white p-3 rounded-xl shadow-lg">
                      <IconComponent className="h-6 w-6 text-[#E63946]" />
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#E63946] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List - Competitive Insight */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Check className="h-4 w-4 text-[#20B2AA] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    variant="ghost" 
                    className="text-[#E63946] hover:text-[#D32F3D] hover:bg-red-50 p-0 h-auto font-semibold group/btn w-full justify-start"
                    onClick={() => openServiceModal(service)}
                    data-testid={`learn-more-${service.id}`}
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            Not sure which treatment is right for you?
          </p>
          <Button 
            size="lg" 
            className="bg-[#E63946] hover:bg-[#D32F3D] text-white px-8 py-6 text-lg font-semibold"
            onClick={openAppointmentModal}
            data-testid="services-section-schedule-btn"
          >
            Book an Appointment
          </Button>
        </div>
      </div>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto relative">
            {/* Close Button - Top Right Corner */}
            <button 
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 p-1.5 rounded-full transition-colors"
              data-testid="close-service-modal"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Modal Content - Horizontal Layout on Desktop */}
            <div className="flex flex-col sm:flex-row">
              {/* Image Section */}
              <div className="relative w-full sm:w-2/5 h-32 sm:h-auto sm:min-h-[280px] flex-shrink-0">
                <img
                  src={selectedService.image}
                  alt={selectedService.title}
                  className="w-full h-full object-cover sm:rounded-l-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-transparent via-transparent to-[#1B1B3A]/60"></div>
                {/* Title on Image for Mobile */}
                <div className="absolute bottom-2 left-3 sm:hidden">
                  <div className="flex items-center gap-2">
                    <div className="bg-[#E63946] p-1.5 rounded-lg">
                      {iconMap[selectedService.icon] && React.createElement(iconMap[selectedService.icon], { className: "h-4 w-4 text-white" })}
                    </div>
                    <h2 className="text-base font-bold text-white drop-shadow-lg">{selectedService.title}</h2>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 p-4 sm:p-6 sm:pr-10">
                {/* Title for Desktop */}
                <div className="hidden sm:flex items-center gap-2 mb-3">
                  <div className="bg-[#E63946] p-2 rounded-lg">
                    {iconMap[selectedService.icon] && React.createElement(iconMap[selectedService.icon], { className: "h-5 w-5 text-white" })}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedService.title}</h2>
                </div>

                {selectedService.fullDescription ? (
                  <div className="space-y-2 mb-3 sm:mb-4">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {selectedService.fullDescription}
                    </p>
                    {selectedService.detailedInfo && (
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                        {selectedService.detailedInfo}
                      </p>
                    )}
                    {selectedService.commonConditions && (
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                        {selectedService.commonConditions}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm leading-relaxed mb-3 sm:mb-4">
                    {selectedService.description}
                  </p>
                )}

                {/* Features - Inline */}
                <div className="mb-3 sm:mb-4">
                  <h3 className="font-semibold text-gray-900 text-xs sm:text-sm mb-2">Our Treatments Include:</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {selectedService.features.map((feature, idx) => (
                      <span key={idx} className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                        <Check className="h-3 w-3 text-[#20B2AA] mr-1" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex gap-2">
                  <Button 
                    size="sm"
                    className="flex-1 bg-[#E63946] hover:bg-[#D32F3D] text-white font-semibold text-xs sm:text-sm h-9"
                    onClick={() => {
                      closeModal();
                      openAppointmentModal();
                    }}
                    data-testid="service-schedule-btn"
                  >
                    Schedule Consultation
                  </Button>
                  <Button 
                    size="sm"
                    variant="outline" 
                    className="border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white text-xs sm:text-sm h-9 px-3"
                    asChild
                  >
                    <a href="tel:+14043552728">
                      <Phone className="mr-1 h-3 w-3" />
                      <span className="hidden sm:inline">(404) 355-2728</span>
                      <span className="sm:hidden">Call</span>
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;
